import { createAsyncThunk, createSlice, isAnyOf } from '@reduxjs/toolkit';
import type { components } from '../../types/apis/dmsm/schema-typescript';
import { dmsmApi } from '../../utils/api-clients/api-clients';
import type { RootState } from '../../store';

type Link = components['schemas']['Link'];
type LinkErrorResponse = Record<string, string>;

interface LinksState {
  links: Record<string, Link>;
  modalErrorMessage: string | undefined;
  modalSuccessMessage: string | undefined;
  createErrorMessage: string | undefined;
}

const initialState: LinksState = {
  links: {},
  modalErrorMessage: undefined,
  modalSuccessMessage: undefined,
  createErrorMessage: undefined
};

export const fetchLinks = createAsyncThunk(
  'links/fetchLinks',
  async (_, { getState }) => {
    const strategyNumber = (getState() as RootState).phases.strategyNumber;

    const { data, error } = await dmsmApi.fetchClient.request(
      ...dmsmApi.operations['findAllLinksByStrategyId'],
      {
        params: {
          path: {
            strategyId: strategyNumber as unknown as number
          }
        }
      }
    );

    if (error) {
      throw new Error('Failed to fetch links');
    }

    return data;
  }
);

export const fetchCreateLink = createAsyncThunk<
  Link,
  Link,
  { rejectValue: LinkErrorResponse }
>('links/create', async (link, { getState, rejectWithValue }) => {
  const strategyNumber = (getState() as RootState).phases.strategyNumber;

  const { data, error } = await dmsmApi.fetchClient.request(
    ...dmsmApi.operations['createLink'],
    {
      params: {
        path: {
          strategyId: strategyNumber as unknown as number
        }
      },
      body: link
    }
  );

  if (error) {
    return rejectWithValue(error as LinkErrorResponse);
  }

  return data;
});

export const fetchEditLink = createAsyncThunk<
  Link,
  Link,
  { rejectValue: LinkErrorResponse }
>('links/edit', async (link, { rejectWithValue }) => {
  const { data, error } = await dmsmApi.fetchClient.request(
    ...dmsmApi.operations['editLink'],
    {
      params: {
        path: {
          linkId: link.linkId as number
        }
      },
      body: link
    }
  );

  if (error) {
    return rejectWithValue(error as LinkErrorResponse);
  }

  return data;
});

export const fetchDeleteLink = createAsyncThunk<
  { linkId: number },
  number,
  { rejectValue: LinkErrorResponse }
>('links/delete', async (linkId, { rejectWithValue }) => {
  const { error } = await dmsmApi.fetchClient.request(
    ...dmsmApi.operations['deleteLink'],
    {
      params: {
        path: {
          linkId
        }
      }
    }
  );

  if (error) {
    return rejectWithValue(error as LinkErrorResponse);
  }

  return { linkId };
});

const clearModalMessages = (state: LinksState) => {
  state.modalErrorMessage = undefined;
  state.modalSuccessMessage = undefined;
  state.createErrorMessage = undefined;
};

const linksSlice = createSlice({
  name: 'linksSlice',
  initialState,
  reducers: {
    clearLinksModalMessages: (state) => clearModalMessages(state)
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLinks.pending, (state) => {
        // Initialize object
        state.links = {};
      })
      .addCase(fetchLinks.fulfilled, (state, action) => {
        action.payload.forEach((link) => {
          state.links[link.linkId!] = link;
        });
      })
      .addCase(fetchDeleteLink.fulfilled, (state, action) => {
        delete state.links[action.payload.linkId];
      })
      .addCase(fetchEditLink.rejected, (state, action) => {
        state.modalErrorMessage = `Failed to update link - ${
          action.payload?.message || action.payload?.url
        }`;
      })
      .addCase(fetchDeleteLink.rejected, (state, action) => {
        state.modalErrorMessage = `Failed to update link - ${action.payload?.message}`;
      })
      .addCase(fetchCreateLink.rejected, (state, action) => {
        state.createErrorMessage = `Failed to create link - ${
          action.payload?.message || action.payload?.url
        }`;
      })
      .addMatcher(
        isAnyOf(fetchEditLink.fulfilled, fetchCreateLink.fulfilled),
        (state, action) => {
          state.links[action.payload.linkId!] = action.payload;
          state.modalSuccessMessage = 'Link saved successfully';
        }
      )
      .addMatcher(
        isAnyOf(
          fetchCreateLink.pending,
          fetchEditLink.pending,
          fetchLinks.pending,
          fetchDeleteLink.pending
        ),
        (state) => {
          clearModalMessages(state);
        }
      );
  }
});

export const { clearLinksModalMessages } = linksSlice.actions;

export const linksReducer = linksSlice.reducer;
